import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, X, ChevronRight, ChevronLeft, Eye, Users, Check } from "lucide-react";
import { Button, StatusPill, Select } from "@/components/kit";
import { Txt } from "@/components/Txt";

const LEVELS = ["none", "view", "edit", "full"];
const BOARD_POSITIONS = ["President", "Vice President", "Secretary", "Treasurer"];
const SELECTED_STYLE = {
  none: "bg-ink text-cream",
  view: "bg-dark-olive text-cream",
  edit: "bg-gold text-ink",
  full: "bg-olive text-cream",
};

function Segmented({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1 bg-warm-cream rounded-lg p-1" data-testid={testid}>
      {LEVELS.map((l) => {
        const sel = value === l;
        return (
          <button
            key={l}
            data-testid={testid ? `${testid}-${l}` : undefined}
            data-selected={sel ? "true" : "false"}
            onClick={() => onChange(l)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[13px] font-semibold capitalize min-h-[36px] transition-colors ${
              sel ? `${SELECTED_STYLE[l]} shadow ring-2 ring-ink/70` : "text-ink/45 hover:text-ink hover:bg-warm-cream"
            }`}
          >
            {sel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
            {l}
          </button>
        );
      })}
    </div>
  );
}

function RoleEditor({ role, sections, groups, onClose, onSaved }) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || "");
  const [layout, setLayout] = useState(role.layout || "admin");
  const [perms, setPerms] = useState({ ...(role.permissions || {}) });
  const [fieldPerms, setFieldPerms] = useState(JSON.parse(JSON.stringify(role.field_permissions || {})));
  const isNew = !role.role_id;
  const readOnly = role.role_id === "super_admin";

  const setLevel = (sid, lvl) => setPerms((p) => ({ ...p, [sid]: lvl }));
  const toggleField = (sid, field) => {
    setFieldPerms((fp) => {
      const cur = { ...(fp[sid] || {}) };
      cur[field] = cur[field] === false ? true : false;
      return { ...fp, [sid]: cur };
    });
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the role a name.");
    const payload = { name, description, layout, permissions: perms, field_permissions: fieldPerms };
    try {
      if (isNew) await api.post("/roles", payload);
      else await api.put(`/roles/${role.role_id}`, payload);
      toast.success(isNew ? "Role created" : "Role saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save role");
    }
  };

  const [confirmReset, setConfirmReset] = useState(false);
  const resetToDefault = async () => {
    try {
      await api.post(`/roles/${role.role_id}/reset`);
      toast.success("Role reset to its default permissions");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset role");
    }
  };

  return (
    <div data-testid="role-editor">
      <button onClick={onClose} data-testid="close-role-editor" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3"><ChevronLeft className="w-4 h-4" /> Back to Roles &amp; access</button>
      <div className="text-[12px] uppercase tracking-widest text-gold font-bold">{isNew ? "New role" : "Editing role"}</div>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold leading-tight mb-6">{name || "Untitled role"}</h1>
      <div className="max-w-3xl space-y-6">
          {readOnly && (
            <div className="bg-maroon/10 border border-maroon/30 rounded-lg p-4 text-[15px] text-ink/80">
              Super Admin has full access to everything and cannot be edited.
            </div>
          )}
          <div className="grid gap-3">
            <label className="text-[14px] font-semibold">Role name</label>
            <input data-testid="role-name-input" value={name} disabled={readOnly} onChange={(e) => setName(e.target.value)}
              className="bg-cream border border-ink/15 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px]" />
            <label className="text-[14px] font-semibold mt-2">Description</label>
            <textarea value={description} disabled={readOnly} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="bg-cream border border-ink/15 rounded-lg px-4 py-2.5 text-[15px]" />
            <label className="text-[14px] font-semibold mt-2">Home layout</label>
            <div className="flex gap-2">
              {["admin", "app"].map((l) => (
                <button key={l} disabled={readOnly} onClick={() => setLayout(l)}
                  className={`px-4 py-2 rounded-lg text-[14px] font-semibold min-h-[44px] border ${layout === l ? "bg-ink text-cream border-ink" : "border-ink/20"}`}>
                  {l === "admin" ? "Admin (sidebar)" : "Member app"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-zilla text-lg font-semibold mb-1"><Txt id="roles.editor.section_access" screen="Roles & access · Role editor">Section access</Txt></h3>
            <p className="text-[14px] text-ink/60 mb-3"><Txt id="roles.editor.section_access_desc" screen="Roles & access · Role editor">Set each section independently. Sections set to None disappear from this role's navigation.</Txt></p>
            <div className="space-y-2">
              {sections.map((s, i) => {
                const lvl = perms[s.id] || "none";
                const prev = sections[i - 1];
                const showHeader = !prev || prev.group !== s.group;
                const groupLabel = s.group ? ((groups && groups[s.group] && groups[s.group].label) || s.group) : "General";
                return (
                  <div key={s.id}>
                  {showHeader && <div className="text-[12px] font-bold uppercase tracking-widest text-ink/45 mt-4 mb-1 px-1" data-testid={`role-editor-group-${s.group || "general"}`}>{groupLabel}</div>}
                  <div className="bg-cream border border-ink/12 rounded-lg p-3" data-testid={`section-perm-${s.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[15px] font-semibold">{s.label}</div>
                      <Segmented value={lvl} onChange={(v) => !readOnly && setLevel(s.id, v)} testid={`seg-${s.id}`} />
                    </div>
                    {lvl !== "none" && (s.fields || []).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-ink/10">
                        <div className="text-[12px] font-semibold text-ink/55 mb-2 uppercase tracking-wide">Field by field</div>
                        <div className="flex flex-wrap gap-2">
                          {s.fields.map((f) => {
                            const visible = (fieldPerms[s.id]?.[f]) !== false;
                            return (
                              <button key={f} disabled={readOnly} onClick={() => toggleField(s.id, f)}
                                data-testid={`field-${s.id}-${f}`}
                                className={`text-[13px] px-3 py-1.5 rounded-md border ${visible ? "bg-olive/20 border-olive/40 text-ink" : "bg-transparent border-ink/20 text-ink/40 line-through"}`}>
                                {f.replace(/_/g, " ")}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                );
              })}
            </div>
          </div>

          {!readOnly && (
            <div className="flex flex-wrap gap-3 sticky bottom-0 bg-warm-cream py-3">
              <Button data-testid="save-role-button" onClick={save} className="px-6 py-3 min-h-[48px]">
                {isNew ? "Create role" : "Save role"}
              </Button>
              <Button variant="secondary" onClick={onClose} className="px-6 py-3 min-h-[48px]">Cancel</Button>
              {!isNew && role.is_system && (
                <Button variant="secondary" data-testid="reset-role-button" onClick={() => setConfirmReset(true)}
                  className="px-6 py-3 min-h-[48px] ml-auto text-maroon">Reset to default</Button>
              )}
            </div>
          )}
          {confirmReset && (
            <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="reset-role-modal" onClick={() => setConfirmReset(false)}>
              <div className="bg-cream rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-zilla text-xl font-semibold mb-2">Reset “{role.name}” to default?</h3>
                <p className="text-[14px] text-ink/70 mb-5">This replaces every permission on this built-in role with its original values from the system defaults. Any changes you made will be lost. People keep the role — only its permissions change.</p>
                <div className="flex gap-3 justify-end">
                  <Button variant="secondary" onClick={() => setConfirmReset(false)} className="px-5 py-2.5 min-h-[44px]">Cancel</Button>
                  <Button data-testid="confirm-reset-role" onClick={() => { setConfirmReset(false); resetToDefault(); }} className="px-5 py-2.5 min-h-[44px]">Reset to default</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
}

function PersonAssignEditor({ person, roles, onClose, onSaved }) {
  const assignable = roles.filter((r) => r.role_id !== "super_admin");
  const [selected, setSelected] = useState(new Set(person.role_ids || []));
  const [position, setPosition] = useState(person.board_position || "");
  const [positions, setPositions] = useState(BOARD_POSITIONS);
  const [newPos, setNewPos] = useState("");
  useEffect(() => { api.get("/board-positions").then((r) => setPositions(r.data.positions || BOARD_POSITIONS)).catch(() => {}); }, []);
  const addPosition = async () => {
    const v = newPos.trim();
    if (!v) return;
    const next = positions.includes(v) ? positions : [...positions, v];
    try { const r = await api.put("/board-positions", { positions: next }); setPositions(r.data.positions); setPosition(v); setNewPos(""); toast.success(`Added "${v}"`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add position"); }
  };

  const toggle = (rid) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(rid) ? next.delete(rid) : next.add(rid);
      return next;
    });

  const save = async () => {
    const ids = assignable.filter((r) => selected.has(r.role_id)).map((r) => r.role_id);
    try {
      await api.put(`/users/${person.user_id}/roles`, { role_ids: ids });
      await api.put(`/users/${person.user_id}/board-position`, { position });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div data-testid="person-assign-editor">
      <button onClick={onClose} data-testid="close-person-editor" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3"><ChevronLeft className="w-4 h-4" /> Back to Roles &amp; access</button>
      <div className="text-[12px] uppercase tracking-widest text-gold font-bold">Assign roles</div>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold leading-tight">{person.name}</h1>
      <div className="text-[14px] text-ink/60 mb-6">{person.email}</div>
      <div className="max-w-2xl space-y-4">
          {person.is_super_admin && (
            <div className="bg-maroon/10 border border-maroon/30 rounded-lg p-4 text-[15px] text-ink/80">
              This account is a Super Admin and already has full access. Roles below still apply if Super Admin is ever removed.
            </div>
          )}
          <p className="text-[14px] text-ink/60">
            Assign any combination of roles. Access is the broadest of all assigned roles — if one role can edit a section and another can only view it, this person can edit it.
          </p>
          <div className="bg-warm-cream border border-ink/15 rounded-lg p-4" data-testid="board-position-block">
            <label className="text-[14px] font-semibold block mb-1">Board position</label>
            <p className="text-[13px] text-ink/55 mb-2">A title shown next to their name. It's a label only — it does not change access. Leave blank for a general board member.</p>
            <Select data-testid="board-position-select" value={position} onChange={(e) => setPosition(e.target.value)}
              className="w-full bg-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
              <option value="">— None (general board member)</option>
              {positions.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>
            <div className="flex gap-2 mt-2" data-testid="add-position-row">
              <input data-testid="new-position-input" value={newPos} onChange={(e) => setNewPos(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPosition(); } }} placeholder="Add a new position, e.g. Membership Chair" className="flex-1 bg-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
              <Button data-testid="add-position-btn" onClick={addPosition} className="px-4 min-h-[42px] text-[14px]">Add</Button>
            </div>
          </div>
          <div className="space-y-2" data-testid="assign-role-checklist">
            {assignable.map((r) => {
              const on = selected.has(r.role_id);
              return (
                <button key={r.role_id} data-testid={`assign-role-${r.role_id}`} onClick={() => toggle(r.role_id)}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg border min-h-[52px] ${on ? "bg-olive/20 border-olive/50" : "bg-cream border-ink/15 hover:bg-warm-cream/60"}`}>
                  <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border ${on ? "bg-olive border-olive" : "border-ink/30"}`}>
                    {on && <Check className="w-3.5 h-3.5 text-ink" />}
                  </span>
                  <span className="min-w-0">
                    <span className="text-[15px] font-semibold flex items-center gap-2">
                      {r.name}
                      <span className="text-[11px] uppercase tracking-wide text-ink/40 font-bold">{r.layout === "admin" ? "admin" : "member"}</span>
                    </span>
                    <span className="block text-[13px] text-ink/60 truncate">{r.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-3 sticky bottom-0 bg-warm-cream py-3">
            <Button data-testid="save-person-roles-button" onClick={save} className="px-6 py-3 min-h-[48px]">Save roles</Button>
            <Button variant="secondary" onClick={onClose} className="px-6 py-3 min-h-[48px]">Cancel</Button>
          </div>
        </div>
      </div>
  );
}

function RolePeopleModal({ role, onClose }) {
  const [people, setPeople] = useState(null);
  useEffect(() => { api.get(`/roles/${role.role_id}/people`).then((r) => setPeople(r.data.people)).catch(() => setPeople([])); }, [role.role_id]);
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="role-people-modal" onClick={onClose}>
      <div className="bg-cream rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-zilla text-xl font-semibold">Who holds &ldquo;{role.name}&rdquo;</h3>
          <button data-testid="role-people-close" onClick={onClose} className="text-ink/50 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        {people === null ? <div className="text-ink/50 text-[15px] py-6 text-center">Loading…</div>
          : people.length === 0 ? <div className="text-ink/50 text-[15px] py-6 text-center">No one holds this role yet.</div>
          : <div className="space-y-1.5" data-testid="role-people-list">
              {people.map((p) => (
                <div key={p.user_id} className="bg-warm-cream rounded-lg px-3 py-2.5">
                  <div className="text-[15px] font-semibold">{p.name || "—"}</div>
                  <div className="text-[13px] text-ink/55">{p.email}{p.seat ? ` · ${p.seat}` : ""}</div>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

function DeleteRoleModal({ role, roles, onClose, onDone }) {
  const [people, setPeople] = useState(null);
  const [reassign, setReassign] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/roles/${role.role_id}/people`).then((r) => setPeople(r.data.people)).catch(() => setPeople([])); }, [role.role_id]);
  const options = roles.filter((r) => r.role_id !== role.role_id);
  const del = async () => {
    if (people && people.length && !reassign) return toast.error("Choose a role to reassign people to.");
    setBusy(true);
    try {
      const qs = people && people.length ? `?reassign_to=${encodeURIComponent(reassign)}` : "";
      await api.delete(`/roles/${role.role_id}${qs}`);
      toast.success("Role deleted"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete role"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="delete-role-modal" onClick={onClose}>
      <div className="bg-cream rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-zilla text-xl font-semibold mb-2">Delete &ldquo;{role.name}&rdquo;?</h3>
        {people === null ? <div className="text-ink/50 py-4">Loading…</div> : (
          <>
            {people.length > 0 ? (
              <div className="space-y-2 mb-4">
                <p className="text-[14px] text-ink/70">{people.length} {people.length === 1 ? "person holds" : "people hold"} this role. Choose a role to move them to so no one loses access.</p>
                <Select data-testid="reassign-role-select" value={reassign} onChange={(e) => setReassign(e.target.value)}
                  className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
                  <option value="">Choose a replacement role…</option>
                  {options.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                </Select>
              </div>
            ) : <p className="text-[14px] text-ink/70 mb-4">No one holds this role. It will be removed permanently.</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <button data-testid="confirm-delete-role" onClick={del} disabled={busy}
                className="bg-maroon text-cream rounded-lg px-4 py-2 min-h-[44px] text-[14px] font-semibold disabled:opacity-50">{busy ? "Deleting…" : "Delete role"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function RolesAccess() {
  const [tab, setTab] = useState("super");
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const [groups, setGroups] = useState({});
  const [supers, setSupers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [people, setPeople] = useState([]);
  const [peopleQ, setPeopleQ] = useState("");
  const [posFilter, setPosFilter] = useState("");
  const [assigning, setAssigning] = useState(null);
  const [editing, setEditing] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [excForm, setExcForm] = useState({ email: "", section: "", level: "view" });
  const [excPicked, setExcPicked] = useState(false);
  const [peopleRole, setPeopleRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);
  const [clearAudit, setClearAudit] = useState(false);
  const [clearText, setClearText] = useState("");

  const load = useCallback(async () => {
    const [r, s, a, e, u] = await Promise.all([
      api.get("/roles"), api.get("/super-admins"), api.get("/audit-log"), api.get("/exceptions"), api.get("/users"),
    ]);
    setRoles(r.data.roles);
    setSections(r.data.sections);
    setGroups(r.data.groups || {});
    setSupers(s.data.super_admins);
    setAudit(a.data.log);
    setExceptions(e.data.exceptions);
    setPeople(u.data.users);
  }, []);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!grantEmail.trim()) return;
    try {
      await api.post("/super-admins/grant", { email: grantEmail.trim() });
      toast.success("Super Admin granted");
      setGrantEmail("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant"); }
  };
  const revoke = async (email) => {
    try {
      await api.post("/super-admins/revoke", { email });
      toast.success("Super Admin removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const addException = async () => {
    if (!excForm.email || !excForm.section) return toast.error("Pick a person and a section.");
    try {
      await api.post("/exceptions", excForm);
      toast.success("Exception set");
      setExcForm({ email: "", section: "", level: "view" });
      setExcPicked(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set exception"); }
  };

  const doClearAudit = async () => {
    try {
      const r = await api.post("/audit-log/clear", { confirmation: clearText });
      toast.success(`Audit log cleared (${r.data.cleared} entries)`);
      setClearAudit(false); setClearText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear the audit log"); }
  };

  const TABS = [
    { id: "super", label: <Txt id="roles.tab.super" screen="Roles & access">Super Admins</Txt> },
    { id: "roles", label: <Txt id="roles.tab.roles" screen="Roles & access">Saved roles</Txt> },
    { id: "people", label: <Txt id="roles.tab.people" screen="Roles & access">People</Txt> },
    { id: "exceptions", label: <Txt id="roles.tab.exceptions" screen="Roles & access">Per-person exceptions</Txt> },
  ];

  const roleName = (rid) => roles.find((r) => r.role_id === rid)?.name || rid;

  if (editing) return <RoleEditor role={editing} sections={sections} groups={groups} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  if (assigning) return <PersonAssignEditor person={assigning} roles={roles} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />;

  return (
    <div>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="roles.title" screen="Roles & access">Roles &amp; access</Txt></h1>
      <p className="text-[17px] text-ink/70 mb-5"><Txt id="roles.subtitle" screen="Roles & access">Who can reach what, and the trail of every change.</Txt></p>

      <div className="flex gap-1 bg-cream border border-ink/12 rounded-xl p-1 mb-6 w-full sm:w-fit overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`roles-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 px-3 sm:px-4 py-2.5 rounded-lg text-[14px] sm:text-[15px] font-semibold min-h-[44px] whitespace-nowrap ${tab === t.id ? "bg-ink text-cream" : "text-ink/70"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "super" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-cream border border-ink/15 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-maroon" />
              <h2 className="font-zilla text-xl font-semibold"><Txt id="roles.super.title" screen="Roles & access">Super Admins</Txt></h2>
            </div>
            <p className="text-[14px] text-ink/60 mb-4"><Txt id="roles.super.desc" screen="Roles & access">No limit on how many. At least two is wise so the association is never locked out.</Txt></p>
            <div className="space-y-2 mb-4" data-testid="super-admin-list">
              {supers.map((a) => (
                <div key={a.user_id} className="flex items-center justify-between gap-3 bg-warm-cream rounded-lg px-3 py-2.5">
                  <div>
                    <div className="text-[15px] font-semibold">{a.name}</div>
                    <div className="text-[13px] text-ink/60">{a.email}{a.seat ? ` · ${a.seat}` : ""}</div>
                  </div>
                  <button data-testid={`revoke-super-${a.email}`} onClick={() => revoke(a.email)}
                    className="text-[13px] text-maroon font-semibold hover:underline">Remove</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="grant-super-email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="email@rosemoorchicago.org"
                className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <Button data-testid="grant-super-button" onClick={grant}>
                <Plus className="w-4 h-4" /> Add
              </Button>
            </div>
          </div>

          <div className="bg-cream border border-ink/15 rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="font-zilla text-xl font-semibold"><Txt id="roles.audit.title" screen="Roles & access">Audit log</Txt></h2>
                <p className="text-[14px] text-ink/60"><Txt id="roles.audit.desc" screen="Roles & access">Every access change — who, to whom, and when.</Txt></p>
              </div>
              <button data-testid="clear-audit-log-btn" onClick={() => { setClearText(""); setClearAudit(true); }}
                className="shrink-0 text-[13px] text-maroon font-semibold border border-maroon/40 rounded-lg px-3 py-2 min-h-[40px] hover:bg-maroon/5">Clear log</button>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="audit-log">
              {audit.length === 0 && <div className="text-ink/50 text-[15px] py-6 text-center">No changes logged yet.</div>}
              {audit.map((l) => (
                <div key={l.id} className="border-b border-ink/8 pb-2 last:border-0">
                  <div className="text-[14px]"><strong>{l.actor_email}</strong> · {l.action.replace(/_/g, " ")} {l.target_email !== "-" && <>→ {l.target_email}</>}</div>
                  <div className="text-[12px] text-ink/50">{new Date(l.timestamp).toLocaleString()}{l.detail ? ` · ${l.detail}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "roles" && (
        <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-ink/12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-zilla text-xl font-semibold"><Txt id="roles.saved.title" screen="Roles & access">Saved roles</Txt></h2>
              <p className="text-[14px] text-ink/60"><Txt id="roles.saved.desc" screen="Roles & access">Each is a reusable set of permissions. The next person in a seat inherits it automatically.</Txt></p>
            </div>
            <Button data-testid="create-role-button" className="shrink-0 self-start sm:self-auto whitespace-nowrap"
              onClick={() => setEditing({ name: "", description: "", layout: "admin", permissions: {}, field_permissions: {} })}>
              <Plus className="w-4 h-4" /> <Txt id="roles.saved.create" screen="Roles & access">Create a role</Txt>
            </Button>
          </div>
          <div data-testid="roles-list">
            {roles.map((r) => (
              <div key={r.role_id} data-testid={`role-row-${r.role_id}`} role="button" tabIndex={0} onClick={() => setEditing(r)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0 text-left hover:bg-warm-cream/60 cursor-pointer">
                <div>
                  <div className="text-[16px] font-semibold flex items-center gap-2">
                    {r.name}
                    {r.role_id === "super_admin" && <ShieldCheck className="w-4 h-4 text-maroon" />}
                    {r.is_system && <span className="text-[11px] uppercase tracking-wide text-ink/45 font-bold">system</span>}
                  </div>
                  <div className="text-[14px] text-ink/60">{r.description}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button data-testid={`role-people-${r.role_id}`} onClick={(e) => { e.stopPropagation(); setPeopleRole(r); }}
                    className="text-[13px] text-dark-olive font-semibold underline decoration-dotted underline-offset-2 hover:text-ink">
                    {r.people_count} {r.people_count === 1 ? "person" : "people"}
                  </button>
                  {!r.is_system && r.role_id !== "super_admin" && (
                    <button data-testid={`role-delete-${r.role_id}`} onClick={(e) => { e.stopPropagation(); setDeleteRole(r); }}
                      className="text-ink/40 hover:text-maroon" title="Delete role"><Trash2 className="w-4 h-4" /></button>
                  )}
                  <ChevronRight className="w-5 h-5 text-ink/40" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {peopleRole && <RolePeopleModal role={peopleRole} onClose={() => setPeopleRole(null)} />}
      {clearAudit && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="clear-audit-modal" onClick={() => setClearAudit(false)}>
          <div className="bg-cream rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-zilla text-xl font-semibold mb-2">Clear the entire audit log?</h3>
            <p className="text-[14px] text-ink/70 mb-4">This permanently deletes every audit entry. A data reset never does this — only this action can. One new entry will be written recording that you cleared it. Type <strong>CLEAR AUDIT LOG</strong> to confirm.</p>
            <input data-testid="clear-audit-input" value={clearText} onChange={(e) => setClearText(e.target.value)}
              placeholder="CLEAR AUDIT LOG" className="w-full bg-warm-cream border border-ink/20 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mb-4" />
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setClearAudit(false)} className="px-5 py-2.5 min-h-[44px]">Cancel</Button>
              <Button data-testid="confirm-clear-audit" disabled={clearText.trim().toUpperCase() !== "CLEAR AUDIT LOG"} onClick={doClearAudit} className="px-5 py-2.5 min-h-[44px]">Clear log</Button>
            </div>
          </div>
        </div>
      )}
      {deleteRole && <DeleteRoleModal role={deleteRole} roles={roles} onClose={() => setDeleteRole(null)} onDone={() => { setDeleteRole(null); load(); }} />}

      {tab === "people" && (
        <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-ink/12">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-maroon" />
              <h2 className="font-zilla text-xl font-semibold"><Txt id="roles.people.title" screen="Roles & access">People</Txt></h2>
            </div>
            <p className="text-[14px] text-ink/60"><Txt id="roles.people.desc" screen="Roles & access">Assign one or more roles to each account. Access is the broadest of everything assigned.</Txt></p>
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <input data-testid="people-search" value={peopleQ} onChange={(e) => setPeopleQ(e.target.value)} placeholder="Search people by name, email, or role…" className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]" />
              <Select data-testid="people-position-filter" value={posFilter} onChange={(e) => setPosFilter(e.target.value)}
                className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px] sm:w-56">
                <option value="">All board positions</option>
                {BOARD_POSITIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                <option value="__none">General board member (no title)</option>
              </Select>
            </div>
          </div>
          <div data-testid="people-list">
            {(() => {
              const ql = peopleQ.trim().toLowerCase();
              let list = people;
              if (posFilter === "__none") list = list.filter((p) => !p.board_position);
              else if (posFilter) list = list.filter((p) => (p.board_position || "") === posFilter);
              if (ql) list = list.filter((p) => `${p.name || ""} ${p.email || ""} ${p.seat || ""} ${p.board_position || ""} ${(p.role_ids || []).map(roleName).join(" ")}`.toLowerCase().includes(ql));
              return (<>
                {list.length === 0 && <div className="text-ink/50 text-[15px] py-8 text-center">No accounts match.</div>}
                {list.map((p) => (
              <button key={p.user_id} data-testid={`person-row-${p.email}`} onClick={() => setAssigning(p)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0 text-left hover:bg-warm-cream/60">
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold flex items-center gap-2">
                    {p.name}
                    {p.board_position && <span data-testid={`person-position-${p.email}`} className="text-[12px] px-2 py-0.5 rounded-full bg-gold/25 text-gold-ink font-semibold">{p.board_position}</span>}
                    {p.is_super_admin && <ShieldCheck className="w-4 h-4 text-maroon" title="Super Admin" />}
                  </div>
                  <div className="text-[13px] text-ink/60 truncate">{p.email}{p.seat ? ` · ${p.seat}` : ""}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`person-roles-${p.email}`}>
                    {p.is_super_admin && (
                      <span className="text-[12px] px-2 py-0.5 rounded bg-maroon/15 border border-maroon/40 text-maroon font-semibold inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Super Admin
                      </span>
                    )}
                    {(() => {
                      // For a Super Admin, don't show the fallback "General Member" tag —
                      // it's misleading next to the Super Admin chip. Real extra roles still show.
                      const extra = (p.role_ids || []).filter((rid) => !(p.is_super_admin && rid === "general_member"));
                      if (!p.is_super_admin && extra.length === 0) return <span className="text-[12px] text-ink/40">No roles</span>;
                      return extra.map((rid) => (
                        <span key={rid} className="text-[12px] px-2 py-0.5 rounded bg-olive/20 border border-olive/40">{roleName(rid)}</span>
                      ));
                    })()}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-ink/40 shrink-0" />
              </button>
                ))}
              </>);
            })()}
          </div>
        </div>
      )}

      {tab === "exceptions" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-cream border border-ink/15 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2"><Eye className="w-5 h-5 text-maroon" /><h2 className="font-zilla text-xl font-semibold"><Txt id="roles.exceptions.title" screen="Roles & access">One person, one exception</Txt></h2></div>
            <p className="text-[14px] text-ink/60 mb-4"><Txt id="roles.exceptions.desc" screen="Roles & access">Grant a single person access beyond their role, without changing that role for anyone else.</Txt></p>
            <div className="space-y-3">
              <div className="relative">
                <input data-testid="exception-email" value={excForm.email} onChange={(e) => { setExcForm({ ...excForm, email: e.target.value }); setExcPicked(false); }}
                  placeholder="Search by name or email…" autoComplete="off"
                  className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                {!excPicked && excForm.email.trim().length >= 1 && (() => {
                  const ql = excForm.email.trim().toLowerCase();
                  const matches = people.filter((p) => `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(ql)).slice(0, 8);
                  if (matches.length === 0) return null;
                  return (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-cream border border-ink/15 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="exception-email-matches">
                      {matches.map((p) => (
                        <button key={p.user_id} data-testid={`exception-email-opt-${p.email}`} onClick={() => { setExcForm({ ...excForm, email: p.email }); setExcPicked(true); }}
                          className="w-full text-left px-3 py-2 hover:bg-warm-cream/60 border-b border-ink/8 last:border-0">
                          <div className="text-[14px] font-semibold">{p.name}</div>
                          <div className="text-[12px] text-ink/55">{p.email}</div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <Select data-testid="exception-section" value={excForm.section} onChange={(e) => setExcForm({ ...excForm, section: e.target.value })}
                className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
                <option value="">Choose a section…</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
              <Select data-testid="exception-level" value={excForm.level} onChange={(e) => setExcForm({ ...excForm, level: e.target.value })}
                className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </Select>
              <Button data-testid="add-exception-button" onClick={addException}><Txt id="roles.exceptions.grant_btn" screen="Roles & access">Grant exception</Txt></Button>
            </div>
          </div>
          <div className="bg-cream border border-ink/15 rounded-xl p-5">
            <h2 className="font-zilla text-xl font-semibold mb-3"><Txt id="roles.exceptions.active_title" screen="Roles & access">Active exceptions</Txt></h2>
            <div className="space-y-2" data-testid="exceptions-list">
              {exceptions.length === 0 && <div className="text-ink/50 text-[15px] py-6 text-center">No per-person exceptions yet.</div>}
              {exceptions.map((ex) => (
                <div key={ex.user_id} className="bg-warm-cream rounded-lg px-3 py-2.5">
                  <div className="text-[15px] font-semibold">{ex.name} <span className="text-ink/50 font-normal text-[13px]">{ex.email}</span></div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {Object.entries(ex.exceptions).map(([sec, lvl]) => (
                      <span key={sec} className="text-[12px] px-2 py-0.5 rounded bg-olive/20 border border-olive/40">
                        {sec.replace(/_/g, " ")}: {lvl}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
